import React, { useState } from 'react'
import { Box, Button, Divider, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import { AGE, C, primaryButtonSx, tnum } from './tokens'
import { NEXT_STEP, canAssign, canMoveTo, canSendToFirstDelivery, canSendToRushmyfood, isActive, isStale, money } from './model'
import { ChannelBadge, PersonAvatar, ZeroTotalFlag } from './Badges'
import AssignDriverMenu from './AssignDriverMenu'
import { useDispatchUi } from './context'

export const mapsUrl = full => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(full)}`
const stop = e => e.stopPropagation()

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

export function AssignButton({ view, prominent, small, iconOnly }) {
  return (
    <AssignDriverMenu views={[view]}>
      {open => iconOnly ? (
        <Tooltip title="Assign driver" arrow>
          <IconButton size="small" onClick={open} aria-label="Assign driver" sx={{ p: 0.5, color: prominent ? C.ink : C.muted, '&:hover': { bgcolor: C.mutedBg } }}>
            <PersonAddAlt1OutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          onClick={open}
          startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: '15px !important' }} />}
          sx={
            prominent
              ? { ...primaryButtonSx, height: small ? 24 : 28, px: 1.25, fontSize: 12, whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: 0.5 } }
              : {
                  height: small ? 24 : 28, px: 1, fontSize: 12, fontWeight: 500, textTransform: 'none', color: C.muted, borderRadius: '8px', whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: C.mutedBg, color: C.text }, '& .MuiButton-startIcon': { mr: 0.5 },
                }
          }
        >
          Assign driver
        </Button>
      )}
    </AssignDriverMenu>
  )
}

function Stack2({ compact, children }) {
  return (
    <Box sx={{ display: 'flex', minWidth: 0, ...(compact ? { alignItems: 'center', gap: 1, '& > *:first-of-type': { flexShrink: 0 } } : { flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }) }}>
      {children}
    </Box>
  )
}

/** Channel and driver in one place, or the action that fills them. */
export function DeliveryCell({ view, compact }) {
  const ui = useDispatchUi()
  if (view.type === 'PICKUP') {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: C.muted }}>
        <ShoppingBagOutlinedIcon sx={{ fontSize: 15 }} />
        {view.typeLabel === 'Pickup' ? 'Customer pickup' : view.typeLabel}
      </Box>
    )
  }
  if (view.driver) {
    return (
      <Stack2 compact={compact}>
        <ChannelBadge channel={view.channel} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <PersonAvatar name={view.driver.name} photo={view.driver.photo} size={20} />
          <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.text, ...ellipsis }}>{view.driver.name}</Box>
        </Box>
      </Stack2>
    )
  }
  if (view.awaitingCourier && isActive(view)) {
    return (
      <Stack2 compact={compact}>
        {/* A restaurant's board never names First Delivery: to them it's RushMyFood. */}
        <ChannelBadge channel={view.external ? view.channel : ui.firstDelivery ? 'FIRST_DELIVERY' : 'RUSHMYFOOD'} />
        {compact ? (
          // No room for the words on one line; the dot says it, the tooltip spells it out.
          <Tooltip title="Finding courier" arrow>
            <Box component="span" aria-label="Finding courier" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.faint, flexShrink: 0 }} />
          </Tooltip>
        ) : (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, fontSize: 12, color: C.muted }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.faint, flexShrink: 0 }} />
            <Box component="span" sx={ellipsis}>Finding courier</Box>
          </Box>
        )}
      </Stack2>
    )
  }
  if (canAssign(view)) {
    const prominent = !isStale(view, ui.now) && view.status !== 'SCHEDULED'
    if (view.channel === 'UNASSIGNED') return <AssignButton view={view} prominent={prominent} />
    return (
      <Stack2 compact={compact}>
        <ChannelBadge channel={view.channel} />
        <AssignButton view={view} prominent={prominent} small iconOnly={compact} />
      </Stack2>
    )
  }
  // An old-app or email delivery always says whose it was, driver or not.
  return view.external ? <ChannelBadge channel={view.channel} /> : null
}

export function AddressActions({ full, reveal, overlay }) {
  const ui = useDispatchUi()
  return (
    <Box
      component="span"
      onClick={stop}
      className={reveal ? 'dispatch-reveal' : undefined}
      sx={{
        display: 'inline-flex', alignItems: 'center', flexShrink: 0,
        // In a table cell the icons sit on top of the address's right end, so
        // they don't take width from it while hidden.
        ...(overlay && {
          position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pl: 2,
          background: 'linear-gradient(to right, rgba(247,248,250,0), #f7f8fa 14px)',
          '@media (hover: none)': { position: 'static', transform: 'none', pl: 0, background: 'none' },
        }),
      }}
    >
      <Tooltip title="Copy address" arrow>
        <IconButton size="small" aria-label="Copy address" onClick={() => ui.copy(full, 'Address')} sx={{ p: 0.4, color: C.muted }}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in Google Maps" arrow>
        <IconButton size="small" component="a" href={mapsUrl(full)} target="_blank" rel="noopener noreferrer" aria-label="Open in Google Maps" sx={{ p: 0.4, color: C.muted }}>
          <MapOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export function NoAddressChip({ label = 'Pickup · no address' }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 20, px: '6px', borderRadius: '5px', border: `1px dashed ${C.input}`,
        fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', color: C.muted,
      }}
    >
      {label}
    </Box>
  )
}

export function CustomerCell({ view, compact }) {
  const { customer, address } = view
  if (!customer && !address) return <NoAddressChip label={view.type === 'PICKUP' ? `${view.typeLabel} · no customer` : 'No address'} />
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      {customer && !compact && <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.text, ...ellipsis }}>{customer.name}</Box>}
      {address ? (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Tooltip
            arrow
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <span>{address.full}</span>
                {address.details && <span style={{ opacity: 0.75 }}>{address.details}</span>}
              </Box>
            }
          >
            <Box component="span" sx={{ minWidth: 0, ...ellipsis, fontSize: compact ? 13 : 12, color: compact ? C.text : C.muted }}>
              {compact && customer ? `${customer.name} · ` : ''}
              {address.short}
            </Box>
          </Tooltip>
          <AddressActions full={address.full} reveal overlay />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {compact && customer && <Box component="span" sx={{ fontSize: 13, color: C.text, ...ellipsis }}>{customer.name}</Box>}
          <NoAddressChip label={view.typeLabel} />
        </Box>
      )}
    </Box>
  )
}

export function AmountCell({ view, compact }) {
  const zero = view.total === 0
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, whiteSpace: 'nowrap', ...tnum }}>
      {zero ? (
        <ZeroTotalFlag>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{money(0)}</Box>
        </ZeroTotalFlag>
      ) : (
        <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>{money(view.total)}</Box>
      )}
      {!compact && view.tip > 0 && <Box component="span" sx={{ fontSize: 12, color: C.muted }}>tip {money(view.tip)}</Box>}
    </Box>
  )
}

export function RowActions({ view }) {
  const ui = useDispatchUi()
  const [anchor, setAnchor] = useState(null)
  const close = () => setAnchor(null)
  const run = fn => () => {
    close()
    fn()
  }
  const next = NEXT_STEP[view.status] && canMoveTo(view, NEXT_STEP[view.status].to) ? NEXT_STEP[view.status] : null
  const cancellable = canMoveTo(view, 'CANCELLED')
  return (
    <>
      <IconButton
        size="small"
        className="dispatch-reveal"
        aria-label={`Actions for #${view.orderNo}`}
        onClick={e => {
          e.stopPropagation()
          setAnchor(e.currentTarget)
        }}
        sx={{ color: C.muted, ...(anchor ? { opacity: '1 !important' } : {}) }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        onClick={stop}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 190, borderRadius: '10px', border: `1px solid ${C.border}`, boxShadow: '0 12px 32px -8px rgba(16,24,40,0.2)' } }}
        MenuListProps={{ dense: true, sx: { '& .MuiMenuItem-root': { fontSize: 13, borderRadius: '6px', mx: 0.5 } } }}
      >
        <MenuItem onClick={run(() => ui.openOrder(view.id))}>Open details</MenuItem>
        <MenuItem onClick={run(() => ui.copy(`#${view.orderNo}`, 'Order number'))}>Copy order #</MenuItem>
        {next && <MenuItem onClick={run(() => ui.setStatus([view], next.to))}>{next.label}</MenuItem>}
        {ui.firstDelivery && canSendToFirstDelivery(view) && <MenuItem onClick={run(() => ui.requestFirstDelivery([view]))}>Send to First Delivery</MenuItem>}
        {ui.rushmyfood && canSendToRushmyfood(view) && <MenuItem onClick={run(() => ui.requestRushmyfood([view]))}>Send to RushMyFood Delivery</MenuItem>}
        {cancellable && <Divider sx={{ my: 0.5 }} />}
        {cancellable && (
          <MenuItem onClick={run(() => ui.requestCancel([view]))} sx={{ color: `${AGE.late.ink} !important` }}>
            Cancel order
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
